/* Odd Bunch referral page: behaviour for sections/ob-referral.liquid.
 *
 * Everything comes from the customer portal ({data-portal}/api/referral-page/*). The login is
 * the portal's own: the same Recharge code, the same rate limits, and a normal portal session,
 * so someone already signed in to portal.oddbunch.ca lands straight on their link.
 *
 * The reward is flat: the same credit for every friend (data-reward, from the section settings).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-obr]');
  if (!root) return;

  var API = (root.getAttribute('data-portal') || '').replace(/\/+$/, '') + '/api/referral-page';
  var SHARE_TITLE = root.getAttribute('data-share-title') || '';
  var REWARD = Number(root.getAttribute('data-reward'));
  var RESEND_SECONDS = 60;
  var TOAST_MS = 2500;
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  var state = { board: null, me: null, email: '', resendAt: 0, timer: null, toastTimer: null };

  /* ---------- small helpers ---------- */

  function money(n) {
    var r = Math.round(Number(n) * 100) / 100;
    return '$' + (r % 1 === 0 ? String(r) : r.toFixed(2));
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function icon(name, size) {
    var s = el('span', 'obds-icon obds-icon-' + name + (size ? ' obds-icon-' + size : ''));
    s.setAttribute('aria-hidden', 'true');
    return s;
  }
  function srOnly(text) { return el('span', 'obds-sr-only', text); }
  function q(sel, scope) { return (scope || root).querySelector(sel); }
  function qa(sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); }

  /** Every call carries the portal cookies except the public board. */
  function api(path, opts) {
    opts = opts || {};
    var init = { method: opts.method || 'GET', credentials: opts.credentials || 'include' };
    if (opts.body) {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify(opts.body);
    }
    return fetch(API + path, init).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        return { status: r.status, ok: r.ok, data: data || {} };
      });
    });
  }

  /* The portal's own wording for its login errors (src/app/login/page.tsx), so a customer
     reads the same thing here as in the portal. */
  function friendly(code) {
    switch (code) {
      case 'invalid_email': return 'Please enter a valid email address.';
      case 'invalid_code': return "That code isn't right or has expired. Try again.";
      case 'try_again': return "We hit a snag reaching our system. That wasn't your code, so please try again in a moment.";
      case 'code_expired': return 'Your code expired. Use a different email to start over and get a fresh one.';
      case 'not_recognized': return "We couldn't match that email. Double-check it and try again, or contact support if you think this is a mistake.";
      case 'rate_limited':
      case 'too_many_attempts': return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth_not_configured': return 'Login is temporarily unavailable. Please try again shortly.';
      default: return 'Something went wrong. Please try again.';
    }
  }

  function setError(form, code) {
    var p = q('[data-error]', form);
    var input = q('input', form);
    if (!p) return;
    if (code) {
      p.textContent = friendly(code);
      p.hidden = false;
      if (input) input.setAttribute('aria-invalid', 'true');
    } else {
      p.textContent = '';
      p.hidden = true;
      if (input) input.removeAttribute('aria-invalid');
    }
  }

  function busy(button, on) {
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    button.disabled = on;
  }

  /* ---------- steps ---------- */

  var steps = {};
  qa('[data-step]').forEach(function (s) { steps[s.getAttribute('data-step')] = s; });

  function show(name, moveFocus) {
    Object.keys(steps).forEach(function (k) { steps[k].hidden = k !== name; });
    syncSticky();
    if (moveFocus) {
      var h = q('h1', steps[name]);
      if (h) h.focus();
    }
  }

  /* ---------- email step ---------- */

  var emailForm = q('[data-form="email"]');
  var emailInput = q('input', emailForm);
  var emailButton = q('button[type="submit"]', emailForm);

  emailForm.addEventListener('submit', function (e) {
    e.preventDefault();
    setError(emailForm, null);
    var email = emailInput.value.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return setError(emailForm, 'invalid_email');
    busy(emailButton, true);
    api('/request-code', { method: 'POST', body: { email: email } })
      .then(function (r) {
        busy(emailButton, false);
        if (!r.data.ok) return setError(emailForm, r.data.error);
        state.email = email;
        q('[data-sent-to]').textContent = email;
        codeInput.value = '';
        syncVerify();
        setError(codeForm, null);
        // A reused code was sent up to a minute ago; the cooldown still runs from now, which
        // errs on the side of not letting them mint a second code too soon.
        startCooldown(RESEND_SECONDS);
        show('code', true);
      })
      .catch(function () {
        busy(emailButton, false);
        setError(emailForm, 'try_again');
      });
  });

  /* ---------- code step ---------- */

  var codeForm = q('[data-form="code"]');
  var codeInput = q('input', codeForm);
  var verifyButton = q('button[type="submit"]', codeForm);
  var resendButton = q('[data-action="resend"]', codeForm);

  function syncVerify() { verifyButton.disabled = codeInput.value.length < 4; }

  codeInput.addEventListener('input', function () {
    var digits = codeInput.value.replace(/\D+/g, '').slice(0, 6);
    if (digits !== codeInput.value) codeInput.value = digits;
    syncVerify();
  });

  codeForm.addEventListener('submit', function (e) {
    e.preventDefault();
    if (codeInput.value.length < 4) return;
    setError(codeForm, null);
    busy(verifyButton, true);
    api('/verify', { method: 'POST', body: { code: codeInput.value, email: state.email } })
      .then(function (r) {
        if (!r.data.ok) {
          busy(verifyButton, false);
          syncVerify();
          return setError(codeForm, r.data.error);
        }
        return loadMe().then(function (me) {
          busy(verifyButton, false);
          syncVerify();
          if (!me) return setError(codeForm, 'try_again');
          showLink(true);
        });
      })
      .catch(function () {
        busy(verifyButton, false);
        syncVerify();
        setError(codeForm, 'try_again');
      });
  });

  q('[data-action="change-email"]', codeForm).addEventListener('click', function () {
    stopCooldown();
    setError(emailForm, null);
    show('email', true);
    emailInput.focus();
  });

  function startCooldown(seconds) {
    stopCooldown();
    state.resendAt = Date.now() + seconds * 1000;
    tick();
    state.timer = window.setInterval(tick, 1000);
  }
  function stopCooldown() {
    if (state.timer) window.clearInterval(state.timer);
    state.timer = null;
  }
  function tick() {
    var left = Math.ceil((state.resendAt - Date.now()) / 1000);
    if (left > 0) {
      resendButton.disabled = true;
      resendButton.textContent = 'Resend code ' + left + 's';
    } else {
      stopCooldown();
      resendButton.disabled = false;
      resendButton.textContent = 'Resend code';
    }
  }

  resendButton.addEventListener('click', function () {
    setError(codeForm, null);
    resendButton.disabled = true;
    api('/resend', { method: 'POST' })
      .then(function (r) {
        if (r.data.ok) return startCooldown(RESEND_SECONDS);
        if (r.data.error === 'too_soon' && r.data.retryAfter) return startCooldown(Number(r.data.retryAfter));
        tick();
        setError(codeForm, r.data.error);
      })
      .catch(function () {
        tick();
        setError(codeForm, 'try_again');
      });
  });

  /* ---------- link step ---------- */

  var linkInput = q('[data-link]');

  function showLink(moveFocus) {
    var me = state.me;
    var name = (me.firstName || '').trim();
    q('[data-greeting]').textContent = name ? 'Hi ' + name + ', your link is ready!' : 'Your link is ready!';
    var hasLink = Boolean(me.referralUrl);
    q('[data-share]').hidden = !hasLink;
    q('[data-no-link]').hidden = hasLink;
    // Shown without the https:// as drawn; the full address is what gets copied and shared.
    if (hasLink) linkInput.value = me.referralUrl.replace(/^https?:\/\//, '');
    stopCooldown();
    show('link', moveFocus);
    renderCard();
  }

  function copyLink() {
    var url = state.me && state.me.referralUrl;
    if (!url) return;
    var done = function () { toast(); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () { legacyCopy(url) && done(); });
    } else if (legacyCopy(url)) {
      done();
    }
  }
  function legacyCopy(text) {
    var t = el('textarea');
    t.value = text;
    t.setAttribute('readonly', '');
    t.className = 'obds-sr-only';
    document.body.appendChild(t);
    t.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(t);
    return ok;
  }
  function toast() {
    var t = q('[data-toast]');
    t.hidden = false;
    if (state.toastTimer) window.clearTimeout(state.toastTimer);
    state.toastTimer = window.setTimeout(function () { t.hidden = true; }, TOAST_MS);
  }

  q('[data-action="copy"]').addEventListener('click', copyLink);
  q('[data-action="share"]').addEventListener('click', function () {
    var url = state.me && state.me.referralUrl;
    if (!url) return;
    if (navigator.share) {
      // Dismissing the share sheet rejects; that is the customer's choice, not an error.
      navigator.share({ title: SHARE_TITLE, url: url }).catch(function () {});
    } else {
      copyLink();
    }
  });

  /* ---------- rewards card ---------- */

  // People drawn on the card before the rest collapse into "+N", as in Figma's "9+" state.
  var MAX_PEOPLE = 9;

  function person(filled) {
    var p = el('span', 'obds-icon obds-icon-xl obr-person' + (filled ? '' : ' obr-person--next'));
    p.setAttribute('aria-hidden', 'true');
    return p;
  }

  // Logged out the card is the offer, drawn by Liquid. Logged in it is what they have earned:
  // one filled person per friend and a dotted one for the next.
  function renderCard() {
    var me = state.me;
    qa('[data-out]').forEach(function (n) { n.hidden = Boolean(me); });
    qa('[data-in]').forEach(function (n) { n.hidden = !me; });
    if (!me) return;

    var count = Math.max(0, Math.floor(Number(me.count) || 0));
    // What Recharge actually credited (the reward has not always been the same amount).
    var earned = Number(me.earned);
    q('[data-total]').textContent = money(Number.isFinite(earned) ? earned : count * REWARD);
    q('[data-total-sub]').textContent =
      'earned so far' + (count ? ' · ' + count + (count === 1 ? ' friend' : ' friends') : '');

    var box = q('[data-friends]');
    var row = el('div', 'obr-friends__row');
    box.textContent = '';
    box.classList.toggle('obr-friends--first', count === 0);
    for (var i = 0; i < Math.min(count, MAX_PEOPLE); i++) row.appendChild(person(true));
    if (count > MAX_PEOPLE) {
      var more = el('span', 'obds-text-eyebrow-bold obr-more-pill', '+' + (count - MAX_PEOPLE));
      more.setAttribute('aria-hidden', 'true');
      row.appendChild(more);
    } else {
      row.appendChild(person(false));
    }
    box.appendChild(row);
    box.appendChild(el('p', 'obds-text-body-regular obr-soft',
      count ? 'Every friend adds another ' + money(REWARD) + '.' : 'Your first friend gets you ' + money(REWARD) + '.'));
  }

  /* ---------- leaderboard ---------- */

  var NAME_CLASS = { rank1: 'obds-text-body-large-regular', rank2: 'obds-text-body-large-regular', rank3: 'obds-text-body-regular', rest: 'obds-text-body-small-regular' };
  var PLACE_CLASS = { rank1: 'obds-text-body-small-regular', rank2: 'obds-text-caption-regular', rank3: 'obds-text-caption-regular', rest: 'obds-text-caption-regular' };
  var FRIENDS_CLASS = { rank1: 'obds-text-body-large-regular', rank2: 'obds-text-body-regular', rank3: 'obds-text-body-small-regular', rest: 'obds-text-body-small-regular' };

  function cell(cls) {
    var c = el('div', 'obr-td' + (cls ? ' ' + cls : ''));
    c.setAttribute('role', 'cell');
    return c;
  }

  function renderBoard() {
    var rows = (state.board && state.board.leaders) || [];
    var table = q('[data-table]');
    var body = q('[data-rows]');
    var empty = q('[data-board-empty]');
    body.textContent = '';
    if (!rows.length) {
      table.hidden = true;
      empty.textContent = 'No referrals yet. Share your link and be first on the board.';
      empty.hidden = false;
    } else {
      empty.hidden = true;
      table.hidden = false;
      // The board shows the top five advocates only.
      rows.slice(0, 5).forEach(function (r) {
        var kind = r.rank === 1 ? 'rank1' : r.rank === 2 ? 'rank2' : r.rank === 3 ? 'rank3' : 'rest';
        var tr = el('div', 'obr-tr obr-tr--' + kind);
        tr.setAttribute('role', 'row');

        var rank = cell();
        if (kind === 'rank1') {
          var crown = el('span', 'obr-crown');
          crown.appendChild(icon('lucide-crown', 'lg'));
          rank.appendChild(crown);
          rank.appendChild(srOnly('1'));
        } else {
          rank.appendChild(el('span', 'obds-text-body-regular', String(r.rank)));
        }

        var who = cell('obr-td--name');
        who.appendChild(el('span', NAME_CLASS[kind], r.name));
        if (r.place) who.appendChild(el('span', PLACE_CLASS[kind] + ' obr-place', r.place));

        var friends = cell('obr-td--friends');
        friends.appendChild(el('span', FRIENDS_CLASS[kind], String(r.friends)));

        tr.appendChild(rank);
        tr.appendChild(who);
        tr.appendChild(friends);
        body.appendChild(tr);
      });
    }
  }

  function boardFailed() {
    q('[data-table]').hidden = true;
    var empty = q('[data-board-empty]');
    empty.textContent = 'The leaderboard is taking a breather. Try again in a minute.';
    empty.hidden = false;
  }

  /* ---------- phone sticky bar ---------- */

  var sticky = q('[data-sticky]');
  var stickyForm = q('[data-form="sticky"]');
  var stickyInput = q('input', stickyForm);
  var heroOut = false;

  // Shown (CSS keeps it to phones) only while the email step is up and the hero's button has
  // scrolled away above the screen.
  function syncSticky() {
    if (sticky) sticky.hidden = !(heroOut && steps.email && !steps.email.hidden);
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      heroOut = !e.isIntersecting && e.boundingClientRect.bottom < 0;
      syncSticky();
    }).observe(emailButton);
  }

  // The two fields are one field.
  stickyInput.addEventListener('input', function () { emailInput.value = stickyInput.value; });
  emailInput.addEventListener('input', function () { stickyInput.value = emailInput.value; });

  // Submit the hero form, scrolled into view first so its error (if any) and the code step
  // that follows are on screen.
  stickyForm.addEventListener('submit', function (e) {
    e.preventDefault();
    emailInput.value = stickyInput.value;
    emailForm.scrollIntoView({ block: 'center' });
    if (emailForm.requestSubmit) emailForm.requestSubmit();
    else emailForm.dispatchEvent(new Event('submit', { cancelable: true }));
  });

  /* ---------- start ---------- */

  function loadMe() {
    return api('/me')
      .then(function (r) {
        state.me = r.ok && r.data && typeof r.data.count === 'number' ? r.data : null;
        return state.me;
      })
      .catch(function () { return null; });
  }

  api('/board', { credentials: 'omit' })
    .then(function (r) {
      if (!r.ok || !Array.isArray(r.data.leaders)) return boardFailed();
      state.board = r.data;
      renderBoard();
    })
    .catch(boardFailed);

  // Already signed in to the portal (or here, earlier): skip straight to the link.
  loadMe().then(function (me) { if (me) showLink(false); });
})();
